import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

const GlobalLoadingContext = createContext(null);

const loadingBridge = {
  show: () => {},
  hide: () => {},
};

export function getLoadingBridge() {
  return loadingBridge;
}

export function GlobalLoadingProvider({ children }) {
  const [count, setCount] = useState(0);

  const show = () => setCount((c) => c + 1);
  const hide = () => setCount((c) => Math.max(0, c - 1));

  useEffect(() => {
    loadingBridge.show = show;
    loadingBridge.hide = hide;
    return () => {
      loadingBridge.show = () => {};
      loadingBridge.hide = () => {};
    };
  }, []);

  const value = useMemo(() => ({ show, hide, isLoading: count > 0 }), [count]);

  return (
    <GlobalLoadingContext.Provider value={value}>
      {children}
      {count > 0 && <GlobalLoaderOverlay />}
    </GlobalLoadingContext.Provider>
  );
}

export function useGlobalLoading() {
  const ctx = useContext(GlobalLoadingContext);
  if (!ctx) throw new Error("useGlobalLoading must be used within GlobalLoadingProvider");
  return ctx;
}

function GlobalLoaderOverlay() {
  const target = document.getElementById("overlay-root") || document.body;
  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] animate-fade-in" />
      <div className="relative z-[1] rounded-xl bg-white shadow-xl px-6 py-4 flex items-center gap-3">
        <svg
          className="h-6 w-6 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
        <span className="text-sm font-medium text-gray-700">Please wait…</span>
      </div>
    </div>,
    target
  );
}
